import type {
  SavingsApplication,
  SavingsApplicationChangelog,
  SavingsApplicationLookup,
  SavingsApplicationPayload
} from '@/interfaces/SavingsApplication.interface';
import {
  SAVINGS_API_VERSION_HEADERS,
  SavingsReferenceResourceClient,
  toSavingsListResponse
} from '../savings-reference-resource';
import type { ListDataResponse } from '../types';

/** Lookups are small curated vocabularies, so one page holds all of each. */
const LOOKUP_PAGE = { page: 1, perPage: 500 };
/** Onboarding history is paged newest first; earlier entries load on demand. */
const CHANGELOG_PAGE_SIZE = 50;

export class ApiSavingsApplications extends SavingsReferenceResourceClient<
  SavingsApplication,
  SavingsApplicationPayload
> {
  protected readonly path = '/savings-applications';
  protected readonly searchFilter = 'search';

  async get(id: number): Promise<SavingsApplication> {
    const res = await this.client.get(`${this.path}/${id}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async listChangelogs(
    id: number,
    page = 1
  ): Promise<ListDataResponse<SavingsApplicationChangelog>> {
    const res = await this.client.getList(
      `${this.path}/${id}/changelogs`,
      { page, perPage: CHANGELOG_PAGE_SIZE },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return toSavingsListResponse<SavingsApplicationChangelog>(res.data);
  }

  listStatuses(): Promise<SavingsApplicationLookup[]> {
    return this.lookup('/savings-application-statuses');
  }

  listTypes(): Promise<SavingsApplicationLookup[]> {
    return this.lookup('/savings-application-types');
  }

  listBrands(): Promise<SavingsApplicationLookup[]> {
    return this.lookup('/savings-customer-brands');
  }

  private async lookup(path: string): Promise<SavingsApplicationLookup[]> {
    const res = await this.client.getList(path, LOOKUP_PAGE, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }
}

export { savingsApplicationsQueryKeys } from './query-keys';
export {
  useSavingsApplicationBrandsQuery,
  useSavingsApplicationChangelogsQuery,
  useSavingsApplicationQuery,
  useSavingsApplicationStatusesQuery,
  useSavingsApplicationTypesQuery
} from './use-savings-applications-queries';
export {
  useSavingsApplicationsCreateMutation,
  useSavingsApplicationsDeleteMutation,
  useSavingsApplicationsUpdateMutation
} from './use-savings-applications-mutations';
export type { SavingsApplicationsUpdateVariables } from './use-savings-applications-mutations';
